// Keep bounds in sync with Automations::AiEmailSchedule.
export const AI_EMAIL_DELAY_HOURS = {
  MIN: 1,
  MAX: 720,
};

export const AI_EMAIL_DELAY_DAYS = {
  MIN: 1,
  MAX: 365,
};

const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

export function browserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch (error) {
    return '';
  }
}

export function defaultAiEmailSchedule() {
  return {
    mode: 'immediate',
    hours: 1,
    days: 1,
    time: '19:00',
    timezone: browserTimeZone(),
  };
}

export function defaultAiEmailActionValue() {
  return {
    inbox_id: null,
    macro_id: null,
    attachment_blob_ids: [],
    schedule: defaultAiEmailSchedule(),
  };
}

export function normalizeAiEmailSchedule(schedule) {
  const defaults = defaultAiEmailSchedule();
  if (!schedule || typeof schedule !== 'object') return defaults;

  const mode = ['immediate', 'hours_after', 'fixed_time'].includes(schedule.mode)
    ? schedule.mode
    : 'immediate';
  const hours = wholeNumber(schedule.hours);
  const days = wholeNumber(schedule.days);

  return {
    mode,
    hours: hours == null ? defaults.hours : hours,
    days: days == null ? defaults.days : days,
    time: normalizeClockTime(schedule.time) || defaults.time,
    timezone: schedule.timezone || defaults.timezone,
  };
}

export function aiEmailScheduleErrorCode(schedule) {
  const source = schedule && typeof schedule === 'object' ? schedule : {};
  const mode = source.mode || 'immediate';
  if (mode === 'immediate') return null;

  if (mode === 'hours_after') {
    const hours = wholeNumber(source.hours);
    if (
      hours == null ||
      hours < AI_EMAIL_DELAY_HOURS.MIN ||
      hours > AI_EMAIL_DELAY_HOURS.MAX
    ) {
      return 'hours';
    }
    return null;
  }

  if (mode === 'fixed_time') {
    const days = wholeNumber(source.days);
    if (
      days == null ||
      days < AI_EMAIL_DELAY_DAYS.MIN ||
      days > AI_EMAIL_DELAY_DAYS.MAX
    ) {
      return 'days';
    }
    const time = normalizeClockTime(source.time);
    if (!TIME_PATTERN.test(time)) return 'time';
    if (fixedTimeInPast(days, time)) return 'past';
    return null;
  }

  return 'hours';
}

export function aiEmailConfigErrorCode(config) {
  const source = Array.isArray(config) ? config[0] : config;
  if (!source || typeof source !== 'object') return 'inbox_macro';

  const inboxId = source.inbox_id;
  const macroId = source.macro_id;
  if (
    inboxId === null ||
    inboxId === undefined ||
    inboxId === '' ||
    macroId === null ||
    macroId === undefined ||
    macroId === ''
  ) {
    return 'inbox_macro';
  }

  return aiEmailScheduleErrorCode(source.schedule);
}

const AI_EMAIL_ERROR_I18N_KEYS = {
  inbox_macro: 'AUTOMATION.ACTION.AI_GENERATED_EMAIL.ERROR_INBOX_MACRO',
  hours: 'AUTOMATION.ACTION.AI_GENERATED_EMAIL.ERROR_HOURS',
  days: 'AUTOMATION.ACTION.AI_GENERATED_EMAIL.ERROR_DAYS',
  time: 'AUTOMATION.ACTION.AI_GENERATED_EMAIL.ERROR_TIME',
  past: 'AUTOMATION.ACTION.AI_GENERATED_EMAIL.ERROR_PAST',
};

export function aiEmailErrorI18nKey(code) {
  return AI_EMAIL_ERROR_I18N_KEYS[code] || '';
}

export function aiEmailErrorBounds(code) {
  if (code === 'days') return AI_EMAIL_DELAY_DAYS;
  return AI_EMAIL_DELAY_HOURS;
}

export function schedulePayload(schedule) {
  const source = schedule && typeof schedule === 'object' ? schedule : {};
  if (!source.mode || source.mode === 'immediate') return null;

  const timezone = browserTimeZone() || source.timezone || '';
  if (source.mode === 'hours_after') {
    return {
      mode: 'hours_after',
      hours: wholeNumber(source.hours),
      timezone,
    };
  }

  if (source.mode === 'fixed_time') {
    return {
      mode: 'fixed_time',
      days: wholeNumber(source.days),
      time: normalizeClockTime(source.time),
      timezone,
    };
  }

  return { mode: source.mode, timezone };
}

export function buildAiEmailActionParams(params) {
  const source = Array.isArray(params) ? params[0] || {} : params || {};
  const attachmentBlobIds = Array.isArray(source.attachment_blob_ids)
    ? source.attachment_blob_ids.filter(Boolean)
    : [];
  const payload = {
    inbox_id: source.inbox_id ?? null,
    macro_id: source.macro_id ?? null,
    attachment_blob_ids: attachmentBlobIds,
  };
  const schedule = schedulePayload(source.schedule);
  if (schedule) payload.schedule = schedule;
  return payload;
}

export function formatClockTime(time) {
  const match = TIME_PATTERN.exec(normalizeClockTime(time) || '');
  if (!match) return '';
  let hours = Number(match[1]);
  const minutes = match[2];
  const suffix = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${hours}:${minutes} ${suffix}`;
}

function wholeNumber(value) {
  if (typeof value === 'number' && Number.isInteger(value)) return value;
  if (typeof value === 'string' && /^\d+$/.test(value.trim())) {
    return Number(value.trim());
  }
  return null;
}

function fixedTimeInPast(days, time) {
  const match = TIME_PATTERN.exec(time);
  if (!match) return true;
  const runAt = new Date();
  runAt.setDate(runAt.getDate() + days);
  runAt.setHours(Number(match[1]), Number(match[2]), 0, 0);
  return runAt.getTime() <= Date.now();
}

function normalizeClockTime(value) {
  const match = /^(\d{2}:\d{2})/.exec(String(value || ''));
  return match ? match[1] : '';
}
